import React, { useState, useEffect } from 'react';
import { TabNav, TabItem } from "@edusense/shared-ui-layout";
import { StatusDisplay } from "@edusense/shared-ui-kit";
import { ComposeModal } from "@edusense/features-tweet-composer";
import { SocialPost } from "@edusense/features-social-post";
import { useAuth } from '@edusense/shared-auth-core';
import { HomeContainer, FeedList as FeedListContainer } from './styles';
import { useTweets } from '@edusense/tweet';
import { useTweetActions } from '../../../../hooks/useTweetActions';
import { UserSection } from '@edusense/features-user-profile';
import { useLocation } from 'react-router';
import { useNavigate } from '../../../../src/hooks/useI18nNavigate';
// Split imports to avoid Rollup binding analysis issues
import { common_loading } from '@paraglide/messages';
import { common_error } from '@paraglide/messages';
import { nav_home } from '@paraglide/messages';
import { actions_following } from '@paraglide/messages';
import { chat_input_placeholder } from '@paraglide/messages';

// Home 컴포넌트에 전달될 속성 정의
export interface HomeProps {
  initialFeedType?: 'recommended' | 'following';
  onFeedTypeChange?: (feedType: 'recommended' | 'following') => void;
  showInfoInitially?: boolean;
}

/**
 * Home 페이지 컴포넌트
 * 
 * 피드 타입 선택 및 피드 목록을 표시하는 메인 페이지 컴포넌트입니다.
 */
const MainPage: React.FC<HomeProps> = ({
  initialFeedType = 'recommended',
  onFeedTypeChange,
  showInfoInitially = true
}) => {
  const { isAuthenticated, user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 🚨 리렌더링 확인 로깅
  console.log(`🔥🔥🔥 MainPage RENDER: ${location.pathname} at ${Date.now()}`);
  console.log(`🔥 MainPage: Component re-rendered successfully!`);
  const [feedType, setFeedType] = useState<'recommended' | 'following'>(initialFeedType);
  
  // 기존 라이브러리 훅 사용 (비로그인 상태에서도 공개 피드 로드)
  const { tweets, loading: isLoading, refresh: loadFeedData } = useTweets({
    autoLoad: !isAuthLoading  // 로그인 여부와 상관없이 피드 로드
  });

  const {
    handleLike,
    handleComment,
    handleTweetSubmit,
    handleCloseModal,
    replyToTweet,
    showComposeModal
  } = useTweetActions(navigate);

  // 트윗 데이터를 SocialPost가 필요로 하는 형태로 변환
  const transformTweetToSocialPost = (tweet: any) => ({
    id: tweet.id,
    content: tweet.content,
    author: {
      id: tweet.author.id,
      name: tweet.author.displayName || tweet.author.name,
      username: tweet.author.username,
      profileImage: tweet.author.avatar || tweet.author.profileImageUrl,
      verified: tweet.author.verified || false
    },
    createdAt: new Date(tweet.createdAt),
    images: tweet.media?.images || [],
    videos: tweet.media?.videos || [],
    likesCount: tweet.likes || 0,
    repostsCount: tweet.retweets || 0,
    repliesCount: tweet.replies || 0,
    viewsCount: tweet.views || 0,
    isLiked: tweet.isLiked || false,
    isReposted: tweet.isRetweeted || false,
  });

  // 트윗 삭제 성공 핸들러
  const handleDeleteSuccess = () => {
    loadFeedData(); // 피드 새로고침
  };

  // 컴포넌트 마운트 시 로그 및 인증 상태 디버깅 + 피드 로드
  useEffect(() => {
    console.log('[HomePage] 컴포넌트가 마운트되었습니다');
    console.log('[HomePage] 인증 상태:', {
      isAuthenticated,
      isAuthLoading,
      user: user ? {
        id: user.id,
        username: user.username,
        name: user.name,
        email: user.email
      } : null
    });

    // 로딩 완료 시 피드 로드 (비로그인 포함)
    if (!isAuthLoading) {
      const status = isAuthenticated ? '로그인됨' : '비로그인';
      console.log(`[HomePage] ${status} - 피드 데이터 로드 시작...`);
      loadFeedData();
    }

    return () => {
      console.log('[HomePage] 컴포넌트가 언마운트되었습니다');
    };
  }, [isAuthenticated, isAuthLoading, user, loadFeedData]);

  // 트윗 생성 완료 시 피드 새로고침
  useEffect(() => {
    const handleNewTweet = () => {
      console.log('[MainPage] 🎉 new-tweet-posted 이벤트 수신! 피드 새로고침 시작...');
      loadFeedData();
    };

    window.addEventListener('new-tweet-posted', handleNewTweet);

    return () => {
      window.removeEventListener('new-tweet-posted', handleNewTweet);
    };
  }, [loadFeedData]);

  // 피드 타입 변경 핸들러
  const handleFeedTypeChange = (id: string) => {
    const newFeedType = id as 'recommended' | 'following';
    setFeedType(newFeedType);
    if (onFeedTypeChange) {
      onFeedTypeChange(newFeedType);
    }
  };

  // 피드 타입 옵션
  const feedOptions: TabItem[] = [
    { id: 'recommended', label: 'Recommended' },
    { id: 'following', label: actions_following() }
  ];

  // 트윗 제출 핸들러 래퍼
  const handleTweetSubmitWithRefresh = async (data: any) => {
    const result = await handleTweetSubmit(data);
    if (result) {
      loadFeedData(); // 성공 시 피드 새로고침
    }
    return result;
  };

  // 로딩 상태 표시
  if (isAuthLoading) {
    return (
      <HomeContainer>
        <StatusDisplay type="loading" message={common_loading()} />
      </HomeContainer>
    );
  }

  return (
    <HomeContainer>
      {/* 사용자 정보 섹션 - 제거됨 */}

      {/* 탭 네비게이션 */}
      <TabNav
        activeTabId={feedType}
        tabs={feedOptions}
        onTabChange={handleFeedTypeChange}
        variant="border"
      />

      {/* 피드 리스트 */}
      {isLoading ? (
        <StatusDisplay type="loading" message={common_loading()} />
      ) : (
        <FeedListContainer data-testid="tweet-feed">
          {tweets.length > 0 ? (
            tweets.map((tweet) => {
              const transformedTweet = transformTweetToSocialPost(tweet);
              return (
                <SocialPost
                  key={tweet.id}
                  {...transformedTweet}
                  showActions={true}
                  onLike={(id, isLiked) => handleLike(id, isLiked)}
                  onComment={(id) => handleComment(id)}
                  onDeleteSuccess={handleDeleteSuccess}
                  path={`/post/${tweet.id}`}
                  data-testid={`social-post-${tweet.id}`}
                />
              );
            })
          ) : (
            <StatusDisplay 
              type="empty" 
              message="표시할 피드가 없습니다.\n사용자를 팔로우하거나 트윗을 작성해보세요." 
            />
          )}
        </FeedListContainer>
      )}

      {/* 답글 작성 모달 */}
      {showComposeModal && replyToTweet && (
        <ComposeModal
          isOpen={showComposeModal}
          onClose={handleCloseModal}
          mode="reply"
          referenceTweet={{
            id: replyToTweet.id,
            content: replyToTweet.content,
            author: {
              id: replyToTweet.author.id,
              name: replyToTweet.author.name,
              username: replyToTweet.author.username,
              profileImage: replyToTweet.author.profileImageUrl || '',
              verified: replyToTweet.author.verified || false
            },
            createdAt: replyToTweet.createdAt
          }}
          onTweetSubmit={handleTweetSubmitWithRefresh}
          placeholder={chat_input_placeholder()}
        />
      )}
    </HomeContainer>
  );
};

export default MainPage;